// ============================================================================
// reactions.js — how a struck unit responds. applyReaction() sets the target's
// state + initial velocities; stepReaction() integrates the fake ragdoll each
// tick (gravity for airborne juggles, tumble rotation, friction) and transitions
// back out. Officers (captain/warlord) have poise: light reactions are damped.
// ============================================================================

import {
  ST_SEEK, ST_FLINCH, ST_LAUNCH, ST_KNOCK, ST_STUN, ST_DEAD,
  RE_FLINCH, RE_LAUNCH, RE_KNOCK, RE_STUN,
  GRAVITY, UNIT_WARLORD, UNIT_CAPTAIN,
} from '../core/constants.js';

const FLINCH_TIME = 0.34;
const STUN_TIME = 1.35;
const KNOCK_GETUP = 0.55;
const DEAD_FADE_START = 1.4;   // s lying still before fading
const DEAD_FADE_TIME = 0.9;

function dirAway(u, srcX, srcZ) {
  let dx = u.x - srcX, dz = u.z - srcZ;
  const d = Math.hypot(dx, dz);
  if (d < 1e-4) { dx = Math.sin(u.heading); dz = Math.cos(u.heading); }
  else { dx /= d; dz /= d; }
  return [dx, dz];
}

/**
 * Apply a combat reaction. Damage is applied by the caller (hits.js); this only
 * governs stagger/knockback/launch physics.
 */
export function applyReaction(world, u, reaction, srcX, srcZ, knock, launch) {
  if (u.state === ST_DEAD) return;
  const [nx, nz] = dirAway(u, srcX, srcZ);
  const m = 1 / Math.max(1, u.mass);           // heavy units barely move
  const officer = u.type === UNIT_WARLORD || u.type === UNIT_CAPTAIN;

  // Officers shrug off flinches (hyper-armour) and soften launches into stuns.
  let react = reaction;
  if (officer) {
    if (react === RE_FLINCH) return;           // no state change, just took damage
    if (react === RE_LAUNCH && u.type === UNIT_WARLORD) react = RE_STUN;
  }

  switch (react) {
    case RE_FLINCH:
      u.state = ST_FLINCH; u.stateT = 0;
      u.vx = nx * 3.5 * m; u.vz = nz * 3.5 * m; u.vy = 0;
      break;
    case RE_STUN:
      u.state = ST_STUN; u.stateT = 0;
      u.vx = nx * 1.6 * m; u.vz = nz * 1.6 * m; u.vy = 0;
      break;
    case RE_LAUNCH:
      u.state = ST_LAUNCH; u.stateT = 0;
      u.vy = (launch || 9) * Math.min(1.1, 0.5 + m);
      u.vx = nx * 2.4 * m; u.vz = nz * 2.4 * m;
      u.tumble = 0; u.tumbleRate = 0; // launched stays roughly upright, spins on knock
      break;
    case RE_KNOCK:
      u.state = ST_KNOCK; u.stateT = 0;
      u.vy = 3.4 + (knock || 8) * 0.15;
      u.vx = nx * (knock || 8) * m; u.vz = nz * (knock || 8) * m;
      u.tumble = 0; u.tumbleRate = (u.x + u.z > 0 ? 1 : -1) * (7 + (knock || 8) * 0.4) * m;
      break;
  }
  // face the attacker while reeling
  u.heading = Math.atan2(-nx, -nz);
}

/** Give a corpse a death-tumble impulse (called when a blow is lethal). */
export function applyDeathImpulse(u, srcX, srcZ, power) {
  const [nx, nz] = dirAway(u, srcX, srcZ);
  const m = 1 / Math.max(1, u.mass);
  u.vy = 3 + power * 0.12;
  u.vx = nx * power * m; u.vz = nz * power * m;
  u.tumbleRate = (Math.sin(u.id * 12.9) > 0 ? 1 : -1) * (6 + power * 0.3) * m;
}

/**
 * Integrate a unit that is mid-reaction. Returns true while the unit is under
 * reaction control (crowd AI should not steer it). dt is the fixed tick.
 */
export function stepReaction(world, u, dt) {
  switch (u.state) {
    case ST_FLINCH: {
      u.stateT += dt;
      u.x += u.vx * dt; u.z += u.vz * dt;
      u.vx *= 0.86; u.vz *= 0.86;
      if (u.stateT >= FLINCH_TIME) { u.state = ST_SEEK; u.stateT = 0; }
      return true;
    }
    case ST_STUN: {
      u.stateT += dt;
      u.x += u.vx * dt; u.z += u.vz * dt;
      u.vx *= 0.82; u.vz *= 0.82;
      if (u.stateT >= STUN_TIME) { u.state = ST_SEEK; u.stateT = 0; }
      return true;
    }
    case ST_LAUNCH: {
      u.stateT += dt;
      u.vy -= GRAVITY * dt;
      u.x += u.vx * dt; u.z += u.vz * dt; u.y += u.vy * dt;
      u.vx *= 0.99; u.vz *= 0.99;
      if (u.y <= 0 && u.vy < 0) {
        u.y = 0;
        // bounce into a knockdown, then recover through stun
        u.state = ST_KNOCK; u.stateT = 0;
        u.vy = 2.2; u.vx *= 0.5; u.vz *= 0.5; u.tumbleRate = 6;
      }
      return true;
    }
    case ST_KNOCK: {
      u.stateT += dt;
      u.vy -= GRAVITY * dt;
      u.x += u.vx * dt; u.z += u.vz * dt; u.y += u.vy * dt;
      u.tumble += u.tumbleRate * dt;
      u.vx *= 0.97; u.vz *= 0.97;
      if (u.y <= 0 && u.vy < 0) {
        u.y = 0; u.vy = 0; u.vx *= 0.4; u.vz *= 0.4;
        u.tumbleRate *= 0.4;
      }
      // once grounded, lie a beat then get up (via stun) — settle tumble to flat
      if (u.y <= 0 && u.stateT >= KNOCK_GETUP) {
        u.state = ST_STUN; u.stateT = STUN_TIME * 0.5; u.tumble = 0; u.tumbleRate = 0;
      }
      return true;
    }
    case ST_DEAD: {
      u.stateT += dt;
      if (u.y > 0 || u.vy > 0) {
        u.vy -= GRAVITY * dt;
        u.x += u.vx * dt; u.z += u.vz * dt; u.y += u.vy * dt;
        u.tumble += u.tumbleRate * dt;
        if (u.y <= 0) { u.y = 0; u.vy = 0; u.vx *= 0.3; u.vz *= 0.3; u.tumbleRate *= 0.2; }
      }
      // fade then request despawn (handled by AI/world by watching fade<=0)
      if (u.stateT > DEAD_FADE_START) {
        u.fade = Math.max(0, 1 - (u.stateT - DEAD_FADE_START) / DEAD_FADE_TIME);
      }
      return true;
    }
  }
  return false;
}
